import { useEffect, useRef, useState } from "react";

import { UploadReference } from "components/molecules/Upload";
import { alert } from "contexts";

interface Params {
  fileExtensions?: Array<string>;
  wrongFileExtensionMessage?: string;
}

const useUpload = ({ fileExtensions, wrongFileExtensionMessage }: Params) => {
  const uploadRef = useRef<UploadReference | null>(null);
  const progressIntervalInstance = useRef<NodeJS.Timer | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("Idle");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!progress || progress < 90 || progress === 100) return;

    if (progressIntervalInstance.current) {
      clearInterval(progressIntervalInstance.current);
    }
    uploadRef?.current?.reset();
    setProgress(100);
    setUploadStatus("Completed");
  }, [progress]);

  const checkFileExtensions = (file: File) => {
    const fileExtension = file.name.split(".").pop();
    if (!fileExtensions) return true;
    return fileExtensions.some((i) => i === `.${fileExtension as string}`);
  };

  const onUploadHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file && !checkFileExtensions(file)) {
      alert({
        title: "Wrong File Extension",
        content: wrongFileExtensionMessage,
      }).then(() => uploadRef.current?.reset());
      return;
    }

    setProgress(0);
    setUploadStatus("Uploading");
    if (!file) {
      setUploadStatus("Failed");
      return;
    }
    setUploadedFile(file);
    progressIntervalInstance.current = setInterval(() => {
      setProgress((prevState) => (prevState < 90 ? prevState + 10 : prevState));
    }, 100);
  };

  const reset = () => {
    clearInterval(progressIntervalInstance.current || undefined);
    setProgress(0);
    setUploadStatus("Idle");
    setUploadedFile(null);
  };

  return {
    uploadRef,
    progress,
    uploadedFile,
    uploadStatus,
    onUploadHandler,
    reset,
  };
};

export default useUpload;
